import {
  type MouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
} from "react";
import FocusLock from "react-focus-lock";
import { RemoveScroll } from "react-remove-scroll";
import { VIEWPORT_MD } from "~/constants";
import { ChevronLeftIcon, CloseIcon } from "~/icons";
import { Box } from "../Box/Box";
import { SafeAreaBottom } from "../SafeArea/SafeArea";
import { Text } from "../Text/Text";
import type { Color } from "../utils/colors";
import styles from "./Modal.module.scss";

export type ModalWidth = "narrow" | "standard" | "large";

interface Props {
  busy?: boolean; // busy will block the ability to close the modal
  children: ReactNode;
  footer?: ReactNode;
  hideHeader?: boolean; // remove header from the dom
  open: boolean;
  title?: string;
  width?: ModalWidth;
  removeGutter?: boolean;
  onClose: () => void;
  isCloseBack?: boolean;
  background?: Color;
}

export function Modal({
  busy,
  children,
  footer,
  hideHeader,
  open,
  title,
  width,
  onClose,
  isCloseBack = false,
  background,
  removeGutter,
}: Props): React.ReactNode | null {
  const modalClassNames = [styles.modalNamespace, styles.modalContainer];

  if (width === "large") {
    modalClassNames.push(styles.wideModal);
  }

  if (width === "narrow") {
    modalClassNames.push(styles.narrowModal);
  }

  const handleClose = useCallback(() => {
    if (busy) return;
    onClose();
  }, [busy, onClose]);

  // Add event listener for Escape key
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !busy) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        handleClose();
      }
    };

    if (open) {
      document.addEventListener("keydown", handleEscapeKey);
    }

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [open, busy, handleClose]);

  const preventClose = (event: MouseEvent) => {
    event.stopPropagation();
  };

  if (!open) return null;

  return (
    <Box>
      <FocusLock group="modal" disabled={!open}>
        <Box
          className={styles.modalBackground}
          onClick={handleClose}
          tabIndex={-1}
          display="flex"
          position="fixed"
          width="full"
          height="full"
          alignItems="center"
          justifyContent="center"
          userSelect="none"
          flexDirection="column"
        >
          <Box
            role="dialog"
            aria-modal="true"
            aria-label={title}
            background={background || "white"}
            className={modalClassNames}
            onClick={preventClose}
            tabIndex={-1}
            width="full"
            height="full"
            display="flex"
            flexDirection="column"
            overflow="hidden"
            style={{ cursor: "default" }}
          >
            {!hideHeader && (
              <ModalHeader
                actionType={isCloseBack ? "back" : "close"}
                onAction={handleClose}
                title={title}
                busy={busy || false}
              />
            )}
            <ModalContent open={open} removeGutter={removeGutter}>
              {children}
            </ModalContent>
            {footer && <ModalFooter>{footer}</ModalFooter>}
          </Box>
        </Box>
      </FocusLock>
    </Box>
  );
}

interface ModalHeaderProps {
  onAction: () => void;
  actionType: "close" | "back";
  title?: string;
  busy: boolean;
}

export function ModalHeader({
  onAction,
  actionType,
  title,
  busy,
}: ModalHeaderProps) {
  const safeAreaInset =
    typeof window !== "undefined" && window.ReactNativeWebView?.safeAreaInset;

  const handleAction = () => {
    if (busy) return;
    onAction();
  };

  return (
    <Box position="relative" paddingY="4" background="primary">
      {/*
       * Inner box is position="relative" so the action button (position="absolute")
       * anchors to it rather than to the full-width outer box, keeping the button
       * within the content width while the green background extends edge-to-edge.
       */}
      <Box
        position="relative"
        style={{ maxWidth: VIEWPORT_MD, margin: "0 auto" }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          style={safeAreaInset ? { paddingTop: `${safeAreaInset.top}px` } : {}}
        >
          <Text
            size="large"
            color="overPrimary"
            font="special"
            weight="strong"
            transform="uppercase"
          >
            {title || "\u00A0"}
          </Text>
        </Box>

        <Box
          position="absolute"
          style={
            actionType === "back"
              ? { top: 0, left: 0, bottom: 0 }
              : { top: 0, right: 0, bottom: 0 }
          }
        >
          <Box
            as="button"
            className={styles.actionButton}
            onClick={handleAction}
            disabled={busy}
            aria-label={actionType === "back" ? "Back" : "Close"}
          >
            {actionType === "back" ? (
              <ChevronLeftIcon
                width={32}
                height={32}
                style={{ color: "var(--color-over-primary)" }}
              />
            ) : (
              <CloseIcon
                width={32}
                height={32}
                style={{ color: "var(--color-over-primary)" }}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function ModalContent(props: {
  open: boolean;
  children: ReactNode;
  removeGutter?: boolean;
}) {
  const modalRef = useRef<HTMLElement>(null);
  return (
    <RemoveScroll ref={modalRef} forwardProps enabled={props.open}>
      <Box
        padding={props.removeGutter ? undefined : ["3", "5"]}
        style={{
          overflowY: "auto",
          flex: 1,
        }}
      >
        <Box height="full">{props.children}</Box>
      </Box>
    </RemoveScroll>
  );
}

function ModalFooter(props: { children: ReactNode }) {
  return (
    <Box
      padding={["3", "5"]}
      style={{ boxShadow: "0px -4px 8px rgba(0, 0, 0, 0.1)" }}
    >
      {props.children}
      <SafeAreaBottom />
    </Box>
  );
}
